/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import {
  BreadcrumbComponent,
  CardComponent,
  DescriptionInputComponent,
  ConfirmationPopup,
} from "../../../../common/components";
import { leadCreate } from "../../../../api/lead";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { initialValues, validationSchema } from "../create-lead/create-lead-helper";
import CreateLeadPrimaryDetails from "../create-lead/create-lead-primary-details/create-lead-primary-details";
import CreateLeadAdditionalDetails from "../create-lead/create-lead-additional-details/create-lead-additional-details";
import CreateLeadAddressDetails from "../create-lead/create-lead-address-details/create-lead-address-details";
import descriptionIcon from "../../../../assets/icons/description.svg";
import "./create-lead.scss";
import { useLeadOwnerOptions } from "../../../../utils/getLeadOwnerOptions";
import AlertIcon from "../../../../assets/icons/alert-icon.svg";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CreateLeadResponsive = (props: any) => {
  const {
    createLeadInitialValues,
    setCreateLeadInitialValues,
    setIsShowCreateLeadSection,
    updateLeadList,
    setAlertOpen,
    setAlertMessage,
    setAlertType,
  } = props;

  const breadcrumbLink = [
    { label: "Lead Management", link: "#", handleClick: () => {} },
    {
      label: "Leads",
      link: "/home/lead-management/lead-details",
      handleClick: () => {
        setIsShowCreateLeadSection(false);
        setLoading(false);
        setCreateLeadInitialValues(initialValues);
        setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0]?.value);
        setSelectedLeadSource("Manual");
        setSelectedLeadStatus("New Lead");
        setSelectedRating("");
        setMaritalStatus(null);
        setInterestedProduct([]);
        setPreviewImage(false);
      },
    },
    { label: "Create Leads", link: "#", handleClick: () => {} },
  ];
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions();
  const [selectedLeadOwner, setSelectedLeadOwner] = useState("");
  useEffect(() => {
    if (LEAD_OWNER_OPTIONS?.length)
      setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0]?.value);
  }, [LEAD_OWNER_OPTIONS]);
  const [selectedInterestedProduct, setInterestedProduct] = useState(
    createLeadInitialValues?.interestedProduct
      ? [createLeadInitialValues?.interestedProduct]
      : []
  );
  const [maritalStatus, setMaritalStatus] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState("Manual");
  const [selectedLeadStatus, setSelectedLeadStatus] = useState("New Lead");
  const [selectedRating, setSelectedRating] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isPreviewImage, setPreviewImage] = useState(false);
  const [isNavigate, setIsNavigate] = useState(false);
  const [showClosedWonConfirm, setShowClosedWonConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | {
    values: any;
    resetForm: any;
    navigate: boolean;
  }>(null);
  const navigateRef = React.useRef(true);
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleCancel = async () => {
    setCreateLeadInitialValues(initialValues);
    setIsShowCreateLeadSection(false);
  };

  const handleSave = async (values: any, resetForm: any, navigate: boolean) => {
    setLoading(true);
    try {
      const formData = new FormData();

      const leadDetails = {
        company: values.company,
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        primaryEmail: values.email,
        secondaryEmail: values.secondaryEmail || null,
        primaryPhoneNumber: values?.primaryPhoneNumber || null,
        secondaryPhoneNumber: values?.secondaryPhoneNumber || null,
        website: values.website || null,
        leadSource: selectedLeadSource || null,
        leadStatus: selectedLeadStatus || null,
        faxNumber: values.fax || null,
        industry: values.industry || null,
        numberOfEmployees: values.numEmployees
          ? Number(values.numEmployees)
          : null,
        annualRevenue: values.annualRevenue || null,
        interestedProducts:
          Array.isArray(selectedInterestedProduct) &&
          selectedInterestedProduct.length > 0
            ? selectedInterestedProduct.join(", ")
            : null,
        confidenceScore: selectedRating || null,
        socialLeadId: values.socialLeadId || null,
        description: values.description || null,
        designation: values.designation || null,
        middleName: values.middleName || null,
        age: parseInt(values.age || 0) || null,
        leadValue: values.leadValue || null,
        maritalStatus: maritalStatus || null,
      };

      Object.entries(leadDetails).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      [
        "streetLine1",
        "streetLine2",
        "city",
        "state",
        "country",
        "zipcode",
      ].forEach((field) => {
        if (values[field]) {
          formData.append(
            `address[${field.replace("Address", "")}]`,
            values[field]
          );
        }
      });
      if (values.imageUrl instanceof File) {
        formData.append("leadImg", values.imageUrl);
      }

      const response = await leadCreate(formData);

      if (response.data.success) {
        setLoading(false);
        setCreateLeadInitialValues(initialValues);
        setAlertMessage("Lead saved successfully!");
        setAlertType("success");
        setAlertOpen(true);
        setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0]?.value);
        setSelectedLeadSource("Manual");
        setSelectedLeadStatus("New Lead");
        setSelectedRating("");
        setMaritalStatus(null);
        setInterestedProduct([]);
        setPreviewImage(false);
        setTimeout(() => {
          if (isNavigate) {
            setIsNavigate(false);
            setIsShowCreateLeadSection(false);
          }
          setCreateLeadInitialValues(initialValues);
          resetForm();
          updateLeadList();
        }, 100);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <>
      <div className="createresponsive-wrapper">
        {/* <div className="breadcrumb-wrapper"> */}
          <BreadcrumbComponent
            breadcrumbLink={breadcrumbLink}
            showBackArrow={true}
            handleBackArrowClick={() => {
              setCreateLeadInitialValues(initialValues);
              setIsShowCreateLeadSection(false);
            }}
          />
        {/* </div> */}
        <div className="lead-create-filter-wrapper">
          <Formik
            initialValues={createLeadInitialValues || initialValues}
            validationSchema={validationSchema(false)}
            enableReinitialize={true}
            onSubmit={(values, { resetForm }) =>
              handleSave(values, resetForm, navigateRef.current)
            }
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              validateField,
              setFieldTouched,
              touched,
              errors,
              resetForm,
            }) => (
              <Form>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000', fontSize: 20 }} />}>
                   <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>
                    Primary Details
                  </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <CreateLeadPrimaryDetails
                      setFieldValue={setFieldValue}
                      values={values}
                      setSelectedLeadOwner={setSelectedLeadOwner}
                      selectedLeadOwner={selectedLeadOwner}
                      setSelectedLeadSource={setSelectedLeadSource}
                      selectedLeadSource={selectedLeadSource}
                      setSelectedLeadStatus={setSelectedLeadStatus}
                      selectedLeadStatus={selectedLeadStatus}
                      setSelectedRating={setSelectedRating}
                      selectedRating={selectedRating}
                      setInterestedProduct={setInterestedProduct}
                      selectedInterestedProduct={selectedInterestedProduct}
                      setPreviewImage={setPreviewImage}
                      isPreviewImage={isPreviewImage}
                      validateField={validateField}
                      setFieldTouched={setFieldTouched}
                      errors={errors}
                      touched={touched}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Additional Details */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000', fontSize: 20 }} />}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>
                    Additional Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <CreateLeadAdditionalDetails
                      setFieldValue={setFieldValue}
                      values={values}
                      setSelectedLeadOwner={setSelectedLeadOwner}
                      selectedLeadOwner={selectedLeadOwner}
                      setSelectedLeadSource={setSelectedLeadSource}
                      selectedLeadSource={selectedLeadSource}
                      setSelectedLeadStatus={setSelectedLeadStatus}
                      selectedLeadStatus={selectedLeadStatus}
                      setSelectedRating={setSelectedRating}
                      selectedRating={selectedRating}
                      setInterestedProduct={setInterestedProduct}
                      selectedInterestedProduct={selectedInterestedProduct}
                      maritalStatus={maritalStatus}
                      setMaritalStatus={setMaritalStatus}
                      touched={touched}
                      validateField={validateField}
                      setFieldTouched={setFieldTouched}
                      errors={errors}
                    />
                    <CardComponent title="Description" icon={descriptionIcon}>
                      <div className="form-grid-three">
                        <DescriptionInputComponent
                          fieldName="description"
                          label="Company Description"
                          placeholder="Add Description"
                          optional={false}
                          disabled={false}
                          value={values.description}
                          onChange={(e: any) =>
                            setFieldValue("description", e?.target?.value)
                          }
                          maxlength={150}
                        />
                      </div>
                    </CardComponent>
                  </AccordionDetails>
                </Accordion>

                {/* Address Information */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000', fontSize: 20 }} />}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>
                    Address Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <CreateLeadAddressDetails
                      setFieldValue={setFieldValue}
                      values={values}
                      setSelectedLeadOwner={setSelectedLeadOwner}
                      selectedLeadOwner={selectedLeadOwner}
                      setSelectedLeadSource={setSelectedLeadSource}
                      selectedLeadSource={selectedLeadSource}
                      setSelectedLeadStatus={setSelectedLeadStatus}
                      selectedLeadStatus={selectedLeadStatus}
                      setSelectedRating={setSelectedRating}
                      selectedRating={selectedRating}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Actions */}
                <Box className="footer">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      if (selectedLeadStatus === "Closed-Won") {
                        setPendingAction({
                          values,
                          resetForm,
                          navigate: false,
                        });
                        setShowClosedWonConfirm(true);
                      } else {
                        setIsNavigate(false);
                        handleSubmit();
                      }
                    }}
                  >
                    Save & New
                  </button>
                  <button
                    type="button"
                    className="cancel-button next-button-wrapper"
                    onClick={() => {
                      if (selectedLeadStatus === "Closed-Won") {
                        setPendingAction({ values, resetForm, navigate: true });
                        setShowClosedWonConfirm(true);
                      } else {
                        setIsNavigate(true);
                        handleSubmit();
                      }
                    }}
                  >
                    Save
                  </button>
                </Box>

                {/* Confirm Popup if Closed Won*/}
                <ConfirmationPopup
                  title="Convert Lead to Customer"
                  open={showClosedWonConfirm}
                  note="Are you sure you want to convert this lead into a customer? All lead information will be transferred to the customer record"
                  onCancel={() => {
                    setShowClosedWonConfirm(false);
                  }}
                  onOkay={() => {
                    setShowClosedWonConfirm(false);
                    console.log("Pending Action:", pendingAction);
                    if (pendingAction) {
                      handleSave(
                        pendingAction.values,
                        pendingAction.resetForm,
                        pendingAction.navigate
                      );
                    }
                  }}
                  buttonText="Confirm"
                  Icon={AlertIcon}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CreateLeadResponsive;
